import * as Phaser from 'phaser';
import { botTurnFeel } from '../../sim/bot/feel.ts';
import {
  playtestSketch,
  type PaceReport,
  type PlaytestKind,
  type PlaytestSketch,
} from '../../sim/bot/playtest.ts';
import type { RacecraftReport } from '../../sim/bot/racecraft.ts';
import type { LiveBotLevel } from '../../sim/bot/seats.ts';
import {
  botDisplayName,
  clampBotKnob,
  cloneBotProfile,
  consistencyRange,
  createBotProfile,
  dialsFromKnobs,
  knobsFromDials,
  knobsOf,
  MIN_SKILL_DIAL,
  type BotDials,
  presetProfileKnobs,
  sanitizeBotName,
  serializeBotProfile,
  suggestBotFilename,
  type BotProfileDocument,
  type BotProfileFlagKey,
  type BotProfileNumberKey,
} from '../../sim/botProfile/index.ts';
import { confirmDialog, promptBotFilename } from '../../ui/bots/dialogs.ts';
import {
  canWriteRepoBot,
  clearBotDraft,
  deleteRepoBot,
  downloadBotFile,
  loadBotDraft,
  saveBotDraft,
  writeRepoBot,
} from '../../ui/bots/persist.ts';
import {
  mountBotWorkshop,
  type BotPlaytestState,
  type PlaytestHistoryEntry,
  type BotWorkshop,
} from '../../ui/bots/workshop.tsx';
import { isFloatingPanelOpen } from '../../ui/floatingPanel.tsx';
import { mountOverlay, showOverlay } from '../../ui/overlay.tsx';
import { isTypingInField } from '../../ui/overlayTyping.ts';
import { overlayStore } from '../../ui/panels.ts';
import { clearEscapeOwner, setEscapeOwner } from '../../ui/pause/escape.ts';
import { createPlaytestClient, type PlaytestClient } from '../bot/playtestClient.ts';
import { findBotCatalogEntry, loadBotCatalog, type BotCatalogEntry } from '../bots/catalog.ts';
import { loadPlayDebugDefaults } from '../debugDefaults.ts';
import { findMapCatalogEntry, loadMapCatalog, type MapCatalogEntry } from '../maps/catalog.ts';
import { clearSceneSearch } from '../mode.ts';
import { setPlayCursor } from '../playCursor.ts';

const HISTORY_LIMIT = 50;
/** Behind the menubar and chart: the start screen's deep water, so the chart reads. */
const BACKDROP_COLOR = 0x0f2a3a;
/** The chart button's map until one is picked from Test: a short, cornered shipped course. */
const DEFAULT_PLAYTEST_MAP = 'battleboats-map';

/** Until this share of a run is done, its own pace is too noisy to time the rest on. */
const ETA_MIN_FRACTION = 0.1;
/** By this share, the estimate is the run's own pace alone. */
const ETA_FULL_FRACTION = 0.35;

type PlaytestRun = {
  /** The map picker's choice. */
  mapId: string | null;
  /** The map this run raced (or is racing) on. */
  raceMapId: string | null;
  status: BotPlaytestState['status'];
  kind: PlaytestKind;
  done: number;
  total: number;
  fraction: number;
  startedAt: number;
  sketch: PlaytestSketch | null;
  /** The bot as this run raced it, for the history. */
  raced: BotProfileDocument | null;
  error: string | null;
};

const IDLE_PLAYTEST: Omit<PlaytestRun, 'mapId'> = {
  raceMapId: null,
  status: 'idle',
  kind: 'solo',
  done: 0,
  total: 0,
  fraction: 0,
  startedAt: 0,
  sketch: null,
  raced: null,
  error: null,
};

export type BotSceneData = {
  profile?: BotProfileDocument;
  /** `bots/<id>.json` the profile came from; absent for a new bot. */
  fileName?: string;
};

function overlayCapturesKeys(): boolean {
  return isTypingInField() || isFloatingPanelOpen() || overlayStore.get().editorDialog !== null;
}

/**
 * Bot workshop: one saved bot profile's knobs behind an editor menubar, with the bot's traits
 * charted in the middle. Nothing is drawn in Phaser (a bot has no hull); the scene owns the
 * document, history, draft, and files, like BoatScene.
 */
export class BotScene extends Phaser.Scene {
  private profile: BotProfileDocument = createBotProfile('expert');
  private fileName: string | null = null;
  private savedJson = '';
  private past: BotProfileDocument[] = [];
  private future: BotProfileDocument[] = [];
  private bots: readonly BotCatalogEntry[] = [];
  /** The profile before a bar drag started; the whole drag commits as one undo step. */
  private dragBefore: BotProfileDocument | null = null;
  private busy = false;
  private workshop: BotWorkshop | null = null;
  private maps: readonly MapCatalogEntry[] = [];
  private playtestClient: PlaytestClient | null = null;
  private playtest: PlaytestRun = { mapId: null, ...IDLE_PLAYTEST };
  /** Wall-clock seconds per race of the last finished run on each map: the prior for the ETA. */
  private secondsPerRace = new Map<string, number>();
  /** Every finished playtest of this bot this session, newest first. */
  private history: PlaytestHistoryEntry[] = [];
  /** The history entry the result row and log show; the newest unless one was picked. */
  private selectedRunId: number | null = null;
  private nextRunNumber = 1;

  constructor() {
    super('BotScene');
  }

  create(data: BotSceneData = {}): void {
    setPlayCursor(false);
    showOverlay(mountOverlay());
    this.cameras.main.setBackgroundColor(BACKDROP_COLOR);

    this.profile = cloneBotProfile(data.profile ?? createBotProfile('expert'));
    this.fileName = data.fileName ?? null;
    this.savedJson = this.fileName ? serializeBotProfile(this.profile) : '';
    this.past = [];
    this.dragBefore = null;
    this.future = [];
    this.busy = false;
    this.playtest = { mapId: this.playtest.mapId, ...IDLE_PLAYTEST };
    this.history = [];
    this.selectedRunId = null;
    this.playtestClient?.destroy();
    this.playtestClient = createPlaytestClient();

    this.workshop?.destroy();
    this.workshop = mountBotWorkshop({
      getState: () => ({
        profile: this.profile,
        fileName: this.fileName,
        bots: this.bots.map((entry) => ({ id: entry.id, label: entry.label })),
        currentBotId: this.fileName?.replace(/\.json$/i, '') ?? null,
        canUndo: this.past.length > 0 || this.dragBefore !== null,
        canRedo: this.future.length > 0,
        canDelete: this.fileName !== null,
        dirty: this.isDirty(),
        playtest: this.playtestState(),
      }),
      actions: {
        onNew: () => void this.handleNew(),
        onSave: () => void this.handleSave(),
        onDelete: () => void this.handleDelete(),
        onClose: () => void this.handleClose(),
        onOpenBot: (id) => void this.handleOpen(id),
        onPreset: (level) => this.startFrom(level),
        onNumber: (key, value) => this.setNumber(key, value),
        onFlag: (key, value) => this.setFlag(key, value),
        onCourseSense: (value) =>
          this.edit((next) => {
            next.knobs.courseSense = value;
            next.dials = null;
          }),
        onDial: (key, value, phase) => this.setDial(key, value, phase),
        onRename: (raw) => this.edit((next) => (next.name = sanitizeBotName(raw))),
        onUndo: () => this.handleUndo(),
        onRedo: () => this.handleRedo(),
        onPlaytest: (mapId) => void this.runPlaytest(mapId),
        onPackPlaytest: () => void this.runPlaytest(undefined, 'pack'),
        onCancelPlaytest: () => this.cancelPlaytest(),
        onSelectRun: (id) => this.selectRun(id),
        onRestoreRun: (id) => this.restoreRun(id),
        onClearRuns: () => this.clearRuns(),
        onPlaytestMap: (mapId) => {
          if (this.playtest.status !== 'running' && findMapCatalogEntry(mapId, this.maps)) {
            this.playtest.mapId = mapId;
            this.workshop?.sync();
          }
        },
      },
    });

    window.addEventListener('keydown', this.onKeyDown, true);
    const escapeToken = setEscapeOwner({
      onEscape: () => {
        void this.handleClose();
        return true;
      },
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      clearEscapeOwner(escapeToken);
      window.removeEventListener('keydown', this.onKeyDown, true);
      this.workshop?.destroy();
      this.workshop = null;
      this.playtestClient?.destroy();
      this.playtestClient = null;
      this.game.canvas.parentElement?.removeAttribute('data-bot-workshop');
    });

    this.syncDom();
    void this.boot();
  }

  private async boot(): Promise<void> {
    // Read before the first sync: that sync saves this session's own draft (a new bot is unsaved).
    const draft = loadBotDraft();
    const [bots, maps] = await Promise.all([loadBotCatalog(), loadMapCatalog()]);

    if (!this.sys.isActive()) {
      return;
    }

    this.bots = bots;
    this.maps = maps;
    this.playtest.mapId ??=
      (findMapCatalogEntry(DEFAULT_PLAYTEST_MAP, maps) ?? maps[0])?.id ?? null;

    this.sync();
    await this.offerDraft(draft);
  }

  /** Offers an unsaved draft of this same file once, on open, when it differs from what opened. */
  private async offerDraft(draft: ReturnType<typeof loadBotDraft>): Promise<void> {
    if (
      !draft ||
      draft.fileName !== this.fileName ||
      serializeBotProfile(draft.profile) === serializeBotProfile(this.profile)
    ) {
      return;
    }

    const resume = await confirmDialog({
      title: 'Resume editing?',
      message: `There are unsaved changes to ${botDisplayName(draft.profile.name)}.`,
      confirmLabel: 'Resume',
      cancelLabel: 'Discard',
    });

    if (!this.sys.isActive()) {
      return;
    }

    if (resume) {
      this.profile = cloneBotProfile(draft.profile);
      this.sync();
    } else {
      clearBotDraft();
    }
  }

  private playtestState(): BotPlaytestState {
    const run = this.playtest;
    const selected =
      run.status === 'running' ? undefined : this.history.find((entry) => entry.id === this.selectedRunId);
    const report = selected?.kind === 'solo' ? selected.report : null;
    const pack = selected?.kind === 'pack' ? selected.report : null;
    const races = report?.races ?? [];
    const status = run.status === 'running' || !selected ? run.status : 'done';

    return {
      maps: this.maps.map((entry) => ({ id: entry.id, label: entry.label })),
      mapId: run.mapId,
      raceMapId: selected?.mapId ?? run.raceMapId,
      status,
      kind: run.status === 'running' ? run.kind : (selected?.kind ?? run.kind),
      done: run.done,
      total: run.total,
      fraction: run.fraction,
      secondsLeft: run.status === 'running' ? this.secondsLeft(run) : null,
      pace: report?.pace ?? pack?.summary.pace ?? null,
      paceRange:
        report && races.length > 0
          ? [
              Math.min(...races.map((race) => race.timeMs)) / report.expertMs,
              Math.max(...races.map((race) => race.timeMs)) / report.expertMs,
            ]
          : null,
      races: pack ? pack.races.length : races.length,
      finished: pack ? pack.races.filter((race) => race.finished).length : races.filter((race) => race.finished).length,
      crashes: pack
        ? pack.races.reduce((sum, race) => sum + race.crashes, 0)
        : races.reduce((sum, race) => sum + race.crashes.length, 0),
      stale: selected !== undefined && JSON.stringify(selected.profile.knobs) !== JSON.stringify(this.profile.knobs),
      error: run.error,
      report,
      pack,
      sketch: selected?.sketch ?? null,
      history: this.history,
      selectedId: selected?.id ?? null,
    };
  }

  /** A pack race (two boats thinking) takes longer than a solo one, so each keeps its own prior. */
  private etaKey(mapId: string, kind: PlaytestKind): string {
    return `${kind}\n${mapId}`;
  }

  /**
   * Seconds left in a run: the last run on this map's pace per race at first, then more and more
   * this run's own pace (elapsed over share done). Null while there is nothing to go on.
   */
  private secondsLeft(run: PlaytestRun): number | null {
    const elapsed = (performance.now() - run.startedAt) / 1000;
    const prior = run.raceMapId ? this.secondsPerRace.get(this.etaKey(run.raceMapId, run.kind)) : undefined;
    const priorTotal = prior !== undefined && run.total > 0 ? prior * run.total : null;
    const ownTotal = run.fraction >= ETA_MIN_FRACTION ? elapsed / run.fraction : null;
    let total: number | null;

    if (ownTotal !== null && priorTotal !== null) {
      const weight = Math.min(1, run.fraction / ETA_FULL_FRACTION);
      total = weight * ownTotal + (1 - weight) * priorTotal;
    } else {
      total = ownTotal ?? priorTotal;
    }

    return total === null ? null : Math.max(0, total - elapsed);
  }

  /**
   * Races the profile on a map in the playtest worker: solo for pace, or against Expert in both
   * seat orders for racecraft (`pack`). Either lands in the history. Nothing is recorded as a
   * finish: bot races never reach Top Lists or `race_runs`.
   */
  private async runPlaytest(mapId?: string, kind: PlaytestKind = 'solo'): Promise<void> {
    const id = mapId ?? this.playtest.mapId;
    const entry = id ? findMapCatalogEntry(id, this.maps) : undefined;
    const client = this.playtestClient;

    if (!entry || !client) {
      return;
    }

    this.playtest = {
      ...IDLE_PLAYTEST,
      mapId: entry.id,
      raceMapId: entry.id,
      status: 'running',
      kind,
      raced: cloneBotProfile(this.profile),
      startedAt: performance.now(),
    };
    const run = this.playtest;
    this.workshop?.sync();

    try {
      const request = {
        map: entry.map,
        mapId: entry.id,
        feel: botTurnFeel({ session: loadPlayDebugDefaults() }),
        knobs: knobsOf(this.profile.knobs),
        onProgress: (progress: { done: number; total: number; fraction: number }) => {
          if (this.playtest === run) {
            run.done = progress.done;
            run.total = progress.total;
            run.fraction = progress.fraction;
            this.workshop?.sync();
          }
        },
      };
      const result: { kind: 'solo'; report: PaceReport } | { kind: 'pack'; report: RacecraftReport } =
        kind === 'pack'
          ? { kind, report: await client.runPack(request) }
          : { kind, report: await client.run(request) };

      if (this.playtest === run) {
        // Races in this run, so a run that reused Expert's time is not timed as a longer one.
        this.secondsPerRace.set(
          this.etaKey(entry.id, kind),
          (performance.now() - run.startedAt) / 1000 / Math.max(1, run.total),
        );
        run.status = 'done';
        run.sketch = playtestSketch(entry.map);
        this.history.unshift({
          id: this.nextRunNumber,
          number: this.nextRunNumber,
          finishedAt: Date.now(),
          mapId: entry.id,
          sketch: run.sketch,
          profile: run.raced!,
          ...result,
        });
        this.nextRunNumber += 1;
        this.selectedRunId = this.history[0]!.id;
      }
    } catch (error) {
      // Cancelled or replaced by a newer run: that run owns the state now.
      if (this.playtest === run) {
        run.status = 'error';
        run.error = error instanceof Error ? error.message : String(error);
      }
    }

    if (this.sys.isActive()) {
      this.workshop?.sync();
    }
  }

  /** Shows an earlier test's result and log. */
  private selectRun(id: number): void {
    if (this.playtest.status === 'running' || !this.history.some((entry) => entry.id === id)) {
      return;
    }

    this.selectedRunId = id;
    this.workshop?.sync();
  }

  /** Puts back the bot as an earlier test raced it: knobs, bars, preset. One undo step. */
  private restoreRun(id: number): void {
    const entry = this.history.find((item) => item.id === id);

    if (!entry) {
      return;
    }

    this.edit((next) => {
      next.basedOn = entry.profile.basedOn;
      next.knobs = { ...entry.profile.knobs };
      next.dials = entry.profile.dials ? { ...entry.profile.dials } : null;
    });
    this.selectedRunId = id;
    this.workshop?.showMessage(`Settings from test #${entry.number} restored.`, 'success');
  }

  private clearRuns(): void {
    this.history = [];
    this.selectedRunId = null;
    this.workshop?.sync();
  }

  private cancelPlaytest(): void {
    if (this.playtest.status !== 'running') {
      return;
    }

    this.playtest = { ...IDLE_PLAYTEST, mapId: this.playtest.mapId };
    this.playtestClient?.cancel();
    this.workshop?.sync();
  }

  private isDirty(): boolean {
    return serializeBotProfile(this.profile) !== this.savedJson;
  }

  /** Applies one edit to a copy; a no-op leaves history alone. */
  private edit(change: (next: BotProfileDocument) => void): void {
    this.endDrag();
    const before = this.profile;
    const next = cloneBotProfile(before);
    change(next);

    if (serializeBotProfile(next) === serializeBotProfile(before)) {
      return;
    }

    this.pushHistory(before);
    this.profile = next;
    this.sync();
  }

  private pushHistory(before: BotProfileDocument): void {
    this.past.push(before);

    if (this.past.length > HISTORY_LIMIT) {
      this.past.shift();
    }

    this.future = [];
  }

  /** A knob set by hand: the profile leaves the simple bars (Custom). */
  private setNumber(key: BotProfileNumberKey, value: number): void {
    this.edit((next) => {
      next.knobs[key] = clampBotKnob(key, value);
      next.dials = null;
    });
  }

  private setFlag(key: BotProfileFlagKey, value: boolean): void {
    this.edit((next) => {
      next.knobs[key] = value;
      next.dials = null;
    });
  }

  /**
   * A dragged bar: every knob is rebuilt from the four bars (a Custom profile starts from the
   * bars its knobs read as). A drag is one undo step: `move` updates live, `end` commits.
   */
  private setDial(key: keyof BotDials, value: number, phase: 'move' | 'end'): void {
    const base = this.profile.dials ?? dialsFromKnobs(this.profile.knobs);
    const [low, high] =
      key === 'skill'
        ? [MIN_SKILL_DIAL, 100]
        : key === 'consistency'
          ? consistencyRange(base.skill)
          : [0, 100];
    const dials: BotDials = { ...base, [key]: Math.round(Math.min(high, Math.max(low, value))) };
    const next = cloneBotProfile(this.profile);
    next.dials = dials;
    next.knobs = knobsFromDials(dials);
    const changed = serializeBotProfile(next) !== serializeBotProfile(this.profile);

    if (changed) {
      this.dragBefore ??= this.profile;
      this.profile = next;
    }

    if (phase === 'end') {
      this.endDrag();
    }

    if (changed || phase === 'end') {
      this.sync();
    }
  }

  /** Commits a drag in progress as one undo step. */
  private endDrag(): void {
    const before = this.dragBefore;
    this.dragBefore = null;

    if (before && serializeBotProfile(before) !== serializeBotProfile(this.profile)) {
      this.pushHistory(before);
    }
  }

  private startFrom(level: LiveBotLevel): void {
    this.edit((next) => {
      const knobs = presetProfileKnobs(level);
      next.basedOn = level;
      next.knobs = knobs;
      next.dials = dialsFromKnobs(knobs);
    });
    this.workshop?.showMessage(`Knobs reset to ${level[0]!.toUpperCase()}${level.slice(1)}.`, 'success');
  }

  private handleUndo(): void {
    this.endDrag();
    const previous = this.past.pop();

    if (!previous) {
      return;
    }

    this.future.push(this.profile);
    this.profile = previous;
    this.sync();
  }

  private handleRedo(): void {
    this.endDrag();
    const next = this.future.pop();

    if (!next) {
      return;
    }

    this.past.push(this.profile);
    this.profile = next;
    this.sync();
  }

  /** Re-renders the chrome and keeps the resume draft in step with unsaved edits. */
  private sync(): void {
    if (this.isDirty()) {
      saveBotDraft(this.profile, this.fileName);
    } else {
      clearBotDraft();
    }

    this.workshop?.sync();
    this.syncDom();
  }

  private syncDom(): void {
    const parent = this.game.canvas.parentElement;
    parent?.setAttribute('data-ready', 'true');
    parent?.setAttribute('data-bot-workshop', this.fileName?.replace(/\.json$/i, '') ?? 'untitled');
  }

  private confirmDiscard(): Promise<boolean> {
    if (!this.isDirty()) {
      return Promise.resolve(true);
    }

    return confirmDialog({
      title: 'Discard changes?',
      message: `${botDisplayName(this.profile.name)} has unsaved changes.`,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
    });
  }

  /** Swaps in another document: fresh history, a clean baseline. */
  private openDocument(profile: BotProfileDocument, fileName: string | null): void {
    clearBotDraft();
    this.profile = cloneBotProfile(profile);
    this.fileName = fileName;
    this.savedJson = fileName ? serializeBotProfile(this.profile) : '';
    this.past = [];
    this.dragBefore = null;
    this.future = [];
    this.cancelPlaytest();
    this.playtest = { ...IDLE_PLAYTEST, mapId: this.playtest.mapId };
    // The history belongs to the bot it tested: another bot starts its own.
    this.history = [];
    this.selectedRunId = null;
    this.sync();
  }

  private async handleNew(): Promise<void> {
    if (this.busy || !(await this.confirmDiscard())) {
      return;
    }

    this.openDocument(createBotProfile('expert'), null);
  }

  private async handleOpen(id: string): Promise<void> {
    const entry = findBotCatalogEntry(id, this.bots);

    if (!entry || this.busy || `${entry.id}.json` === this.fileName || !(await this.confirmDiscard())) {
      return;
    }

    this.openDocument(entry.profile, `${entry.id}.json`);
  }

  private async handleSave(): Promise<void> {
    if (this.busy) {
      return;
    }

    this.busy = true;

    try {
      let target = this.fileName;

      if (!target) {
        target = await promptBotFilename(suggestBotFilename(this.profile.name) ?? 'bot.json');

        if (!target) {
          return;
        }
      }

      if (!canWriteRepoBot()) {
        downloadBotFile(this.profile, target);
        this.workshop?.showMessage('Downloaded. Saving into bots/ needs the dev server.', 'warning');
        return;
      }

      const json = serializeBotProfile(this.profile);

      if (!(await writeRepoBot(target, json))) {
        this.workshop?.showMessage('Save failed.', 'error');
        return;
      }

      this.fileName = target;
      this.savedJson = json;
      this.bots = await loadBotCatalog();
      this.sync();
      this.workshop?.showMessage('Bot saved.', 'success');
    } finally {
      this.busy = false;
    }
  }

  private async handleDelete(): Promise<void> {
    const fileName = this.fileName;

    if (!fileName || this.busy) {
      return;
    }

    const yes = await confirmDialog({
      title: 'Delete bot',
      message: `Delete bots/${fileName}? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });

    if (!yes) {
      return;
    }

    if (!canWriteRepoBot() || !(await deleteRepoBot(fileName))) {
      this.workshop?.showMessage('Delete needs the dev server.', 'error');
      return;
    }

    clearBotDraft();
    this.leave();
  }

  private async handleClose(): Promise<void> {
    if (this.busy || !(await this.confirmDiscard())) {
      return;
    }

    clearBotDraft();
    this.leave();
  }

  /** Back to the start screen's Bots pane. */
  private leave(): void {
    clearSceneSearch();
    this.scene.start('StartScene', { view: 'bots' });
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (overlayCapturesKeys() || !(event.ctrlKey || event.metaKey)) {
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.handleUndo();
    } else if (key === 'y' || (key === 'z' && event.shiftKey)) {
      event.preventDefault();
      this.handleRedo();
    } else if (key === 's') {
      event.preventDefault();
      void this.handleSave();
    }
  };
}
